<template>
    <div v-if="conversion" class="dcc-summary">
        <h2 class="dcc-summary__title">
            {{ title }}
        </h2>

        <div class="dcc-summary__container">
            <h3 class="dcc-summary__label" v-text="$ml.get('dccOriginalAmountLabel')" tabindex="0" />
            <strong class="dcc-summary__value" tabindex="0">{{ formattedOriginalAmount }}</strong>
        </div>

        <div v-if="conversion.CommercialRate !== null" class="dcc-summary__container">
            <h3 class="dcc-summary__label" v-text="$ml.get('dccCommercialRateLabel')" tabindex="0" />
            <strong class="dcc-summary__value" tabindex="0">{{ formattedCommercialRate }}</strong>
        </div>

        <div class="dcc-summary__container">
            <h3 class="dcc-summary__label" v-text="$ml.get('dccAppliedMarkupLabel')" tabindex="0" />
            <strong class="dcc-summary__value" tabindex="0">{{ formattedMarkup }}</strong>
        </div>

        <div class="dcc-summary__container">
            <h3 class="dcc-summary__label" v-text="$ml.get('dccConvertedAmountLabel')" tabindex="0" />
            <strong class="dcc-summary__value" tabindex="0">{{ formattedConvertedAmount }}</strong>
        </div>

        <p class="dcc-summary__explanation" tabindex="0">
            {{ explanation }}
        </p>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moneyHelper from '@/helpers/moneyHelper';
import { resolveCurrencyPresentation, formatConvertedAmount } from '@/helpers/dccCurrencyHelper';

const ORDER_CURRENCY_SYMBOL = 'R$';
const ORDER_CURRENCY_CODE = 'BRL';
const RATE_DECIMALS = 6;

export default {
    name: 'DccSummary',

    computed: {
        ...mapGetters({
            conversion: 'orderStatus/getDcc'
        }),

        /**
         * A tela de status não depende do módulo de DCC do checkout — o comprovante pode ser
         * aberto numa sessão nova, sem cotação viva no store. Resolve nome/símbolo/bandeira
         * pelo mesmo helper, a partir só do que a resposta do status trouxe. A bandeira é da
         * moeda, não do país emissor do cartão.
         */
        currency: (self) => resolveCurrencyPresentation(self.conversion.CurrencyCode),

        currencyName: (self) => self.currency.nameKey
            ? self.$ml.get(self.currency.nameKey)
            : self.currency.code,

        title: (self) => self.$ml
            .with('currencyName', `${self.currencyName} (${self.currency.code})`)
            .get('dccReceiptTitle'),

        explanation: (self) => self.$ml
            .with('markupPercent', moneyHelper.formatToPercent(self.conversion.MarkupPercent))
            .get('dccReceiptMarkupExplanation'),

        formattedOriginalAmount: (self) =>
            `${ORDER_CURRENCY_SYMBOL} ${moneyHelper.formatMinorUnits(self.conversion.OriginalAmount)}`,

        formattedConvertedAmount: (self) => formatConvertedAmount(self.conversion),

        formattedCommercialRate: (self) => self.buildRateText(self.conversion.CommercialRate),

        formattedMarkup: (self) => `${moneyHelper.formatToPercent(self.conversion.MarkupPercent)}% aplicado`,
    },

    methods: {
        /**
         * A cotação já devolve a taxa na direção "1 BRL = X <moeda>" — só formata.
         */
        buildRateText(rate) {
            return `1 ${ORDER_CURRENCY_CODE} = ${moneyHelper.formatToRate(rate, RATE_DECIMALS)} ${this.currency.code}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.dcc-summary {
    background-color: #E5F1FF;
    border-radius: 12px;
    padding: 20px;

    &__container {
        &:not(:last-of-type) {
            margin-bottom: 16px;
        }
    }

    &__title {
        font-size: 16px;
        font-weight: 700;
        color: #2F363E;
        margin: 0 0 16px;
    }

    &__label {
        font-size: 14px;
        font-weight: 500;
        color: #5C6A7B;
        margin: 0 0 4px;
    }

    &__value {
        font-size: 16px;
        font-weight: 600;
        color: #2F363E;
    }

    &__explanation {
        font-size: 14px;
        font-weight: 500;
        color: #5C6A7B;
        margin: 16px 0 0;
    }
}
</style>
